import { AfterViewInit, Component, Input, OnChanges } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { PoreFrac } from 'src/app/perivis/models/poreFrac.model';
import { LinearInterpSingle } from 'src/app/perivis/shared/helpers/linear-interp-single.helper';
import { TrajectoryService } from 'src/app/shared/services/trajectory.service';
import { InternalPressureProfileType, LostReturnsFluidDropIPP } from '../../models/internal-pressure-profile.model';
import { UomQuantityInput } from '../../uom-quantity-input/uom-quantity-input-component';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { lastValueFrom } from 'rxjs';
import { FluidsService } from 'src/app/perical/services/fluids.service';
import { WellComponent } from 'src/app/wellbore-inputs/components/well-configuration/models';

interface LostReturnsFluidDropIppForm {
  type: InternalPressureProfileType.FLUIDDROP;
  lossDepth: UomQuantityInput;
  porePressure: UomQuantityInput;
  fluidDensity: UomQuantityInput;
  marginOfError: UomQuantityInput;
  isPressureEnteredAsPpg: boolean;
}

@Component({
    selector: 'app-fluid-drop-ipp',
    templateUrl: './fluid-drop-ipp.component.html',
    providers: subformComponentProviders(FluidDropIppComponent),
    standalone: false
})
export class FluidDropIppComponent implements AfterViewInit, OnChanges {

  @Input() currentString: any;
  @Input() nextStringMudWeight: number;
  @Input() porePressures: PoreFrac[];
  @Input() userUnits: UserUnitsModel;
  @Input() influxDepthRange: { min: number, max: number };
  @Input() tubulars: WellComponent[];

  private _defaultEmw: number;

  public validators: any;
  public defaultEmw: number;
  public densityLabel: string;
  public densityValidation: { min: number, max: number };
  public marginOfErrorValidation: { min: number, max: number };
  public porePressureValidation: { min: number, max: number };
  public defaultTest = null;

  public form = createForm<LostReturnsFluidDropIPP, LostReturnsFluidDropIppForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(InternalPressureProfileType.FLUIDDROP),
      lossDepth: new UntypedFormControl(null),
      porePressure: new UntypedFormControl(null),
      fluidDensity: new UntypedFormControl(null),
      marginOfError: new UntypedFormControl(null),
      isPressureEnteredAsPpg: new UntypedFormControl(true)
    },
    toFormGroup: (obj: LostReturnsFluidDropIPP | null): LostReturnsFluidDropIppForm | null => {
      if (!obj) {
        return null;
      }

      return {
        type: obj.type,
        lossDepth: { control: obj.lossDepth },
        porePressure: { control: obj.porePressure },
        fluidDensity: { control: obj.fluidDensity },
        marginOfError: { control: obj.marginOfError },
        isPressureEnteredAsPpg: obj.isPressureEnteredAsPpg
      }
    },
    fromFormGroup: (formValue: LostReturnsFluidDropIppForm): LostReturnsFluidDropIPP | null => {
      const { lossDepth, porePressure, fluidDensity, marginOfError, ...commonValues } = formValue;
      return {
        lossDepth: +lossDepth?.control,
        porePressure: +porePressure?.control,
        fluidDensity: +fluidDensity?.control,
        marginOfError: +marginOfError?.control,
        ...commonValues
      }
    }
  });

  constructor(private _trajectoryService: TrajectoryService, private _fluidsService: FluidsService) { }

  ngAfterViewInit(): void {
    this.getEmwDefaultPore(this.influxDepthRange.max);
  }

  ngOnChanges(): void {
    this.densityLabel = unitsLib[this.userUnits.density].symbol;

    switch (this.densityLabel) {
      case 'ppg':
        this.densityValidation = { min: 0.01, max: 25 };
        this.marginOfErrorValidation = { min: 0, max: 10 };
        this.porePressureValidation = { min: 0, max: 25 };
        this._defaultEmw = 10.5;
        break;
      case 'kg/m³':
      case 'g/L':
        this.densityValidation = { min: 1, max: 2500 };
        this.marginOfErrorValidation = { min: 0, max: 1000 };
        this.porePressureValidation = { min: 0, max: 2500 };
        this._defaultEmw = 1258;
        break;
      case 'g/cm³':
      case 'kg/l':
      case 'sg':
        this.densityValidation = { min: 0.00119, max: 3 };
        this.marginOfErrorValidation = { min: 0, max: 1.2 };
        this.porePressureValidation = { min: 0, max: 3 };
        this._defaultEmw = 1.26;
        break;
      case 'g/m³':
        this.densityValidation = { min: 1198, max: 2995660 };
        this.marginOfErrorValidation = { min: 0, max: 1198264 };
        this.porePressureValidation = { min: 0, max: 2995660 };
        this._defaultEmw = 1250000;
        break;
    }

    this.validators = {
      lossDepth: [Validators.min(this.influxDepthRange.min), Validators.max(this.influxDepthRange.max), Validators.required],
      porePressure: [Validators.min(this.porePressureValidation.min), Validators.max(this.porePressureValidation.max), Validators.required],
      fluidDensity: [Validators.min(this.densityValidation.min), Validators.max(this.densityValidation.max), Validators.required],
      marginOfError: [Validators.min(this.marginOfErrorValidation.min), Validators.max(this.marginOfErrorValidation.max), Validators.required]
    };
    
    this.getEmwDefaultPore(this.influxDepthRange.max);
  }

  async getEmwDefaultPore(depth, updatePorePressure = false): Promise<void> {
    if (depth) {
      const tvds = await lastValueFrom(this._trajectoryService.getTvdsFromMds([depth]));
      if (this.porePressures?.length > 1) {
        if (depth < this.currentString?.hangerMd) {
          return;
        }
        this.defaultEmw = +LinearInterpSingle(tvds[0], this.porePressures, this.userUnits).toFixed(2);
      } else {
        this.defaultEmw = this._defaultEmw;
      }
      if (!this.form.formGroup.controls.porePressure.value?.control || updatePorePressure) {
        this.form.formGroup.controls.porePressure.setValue({ control: this.defaultEmw });
      }
    }
  }
  
  public porePressureChange() {
    if (!this.form.formGroup.controls.porePressure.value?.control) {
      this.getEmwDefaultPore(this.form.formGroup.controls.lossDepth.value.control, true);
    }
  }

  public lossDepthChange() {
    if (this.form.formGroup.controls.lossDepth.value) {
      this.getEmwDefaultPore(this.form.formGroup.controls.lossDepth.value.control, true);
      this.updateFluidDensity();
    }
  }

  private updateFluidDensity(): void {
    let annularFluidId = null;
    const currentStringIndex = this.tubulars.findIndex(t => t.id === this.currentString.id);

    for (let i = currentStringIndex; i < this.tubulars.length; i++) {
      const tubular = this.tubulars[i];
      if (tubular.shoeMd >= this.form.formGroup.controls.lossDepth.value.control) {
        annularFluidId = tubular.annularFluidId;
        break;
      }
    }

    if (annularFluidId && annularFluidId !== this.currentString.annularFluidId) {
      this._fluidsService.getFluidById(annularFluidId).subscribe(fluid => {
        this.form.formGroup.controls.fluidDensity.setValue({ control: fluid.state['nominalDensity'] });
      });
    } else {
      this.form.formGroup.controls.fluidDensity.setValue({ control: this.currentString.annularFluid?.state['nominalDensity'] ?? 0 });
    }
  }
}
